import { Button } from "@imi/ui/components/Button/Button.js";
import { Tooltip } from "@imi/ui/components/Tooltip/Tooltip.js";
import { TooltipContent } from "@imi/ui/components/Tooltip/TooltipContent.js";
import { TooltipTrigger } from "@imi/ui/components/Tooltip/TooltipTrigger.js";
import { ProductDetails } from "@src/core/engine/products/types";
import { useTranslation } from "react-i18next";
import styles from "./SubProductCell.module.css";

type SubProductCellProps = {
  onViewClick: () => void;
  product: Pick<ProductDetails, "name">;
  selection: ProductDetails[];
  totalCount: number;
  type: string;
};

export function SubProductCell({
  onViewClick,
  product,
  selection,
  totalCount,
  type,
}: SubProductCellProps) {
  const { t } = useTranslation("product");
  const selectionCount = selection.length;

  if (totalCount === 0) {
    return <span className={styles.EmptySubProducts}>-</span>;
  }

  return (
    <div
      aria-label={t(`selection.subProducts.ariaLabels.group`, {
        interpolation: { escapeValue: false },
        productName: product.name,
        type: t(`selection.subProducts.${type}`).toLowerCase(),
      })}
      className={styles.SubProductCell}
      role="group"
    >
      <span className={styles.TotalCount}>{totalCount}</span>
      <div>
        {selectionCount > 0 && (
          <Tooltip>
            <TooltipTrigger asChild delay={100}>
              <span className={styles.SelectionCount} data-slot="count">
                {selectionCount}
              </span>
            </TooltipTrigger>
            <TooltipContent alignOffset={-5} className={styles.TooltipContent}>
              <h4 className={styles.TooltipTitle}>
                {t(`selection.subProducts.tooltip.title.${type}`, {
                  count: selectionCount,
                })}
              </h4>
              <ol>
                {selection.map((product) => (
                  <li key={product.productId}>{product.name}</li>
                ))}
              </ol>
            </TooltipContent>
          </Tooltip>
        )}
        <Button
          aria-label={t("selection.subProducts.ariaLabels.view", {
            interpolation: { escapeValue: false },
            productName: product.name,
            type: t(`selection.subProducts.${type}`).toLowerCase(),
          })}
          color="neutral"
          onClick={onViewClick}
          size="sm"
          variant="outline"
        >
          {t("selection.subProducts.actions.view")}
        </Button>
      </div>
    </div>
  );
}
